import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import "./earn-new.css";

const EarnNewPage = () => {
    const [selectedCoinFilter, setSelectedCoinFilter] = useState("All Coins");
    const [selectedTermFilter, setSelectedTermFilter] = useState("All Terms");
    const [showCoinDropdown, setShowCoinDropdown] = useState(false);
    const [showTermDropdown, setShowTermDropdown] = useState(false);
    const [openFaqId, setOpenFaqId] = useState(null);

    const coinDropdownRef = useRef(null);
    const termDropdownRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (coinDropdownRef.current && !coinDropdownRef.current.contains(e.target)) {
                setShowCoinDropdown(false);
            }
            if (termDropdownRef.current && !termDropdownRef.current.contains(e.target)) {
                setShowTermDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleFaq = (id) => {
        setOpenFaqId(openFaqId === id ? null : id);
    };

    const coinOptions = ["All Coins", "AGCX", "BTC", "ETH", "SOL", "USDT"];
    const termOptions = ["All Terms", "Flexible", "30 Days", "60 Days"];

    const topCryptoApyList = [
        {
            name: "AGCX",
            fullName: "AGCX / Flexible",
            rate: "10.50%",
            icon: "/images/token-img/token_agce_icon.svg",
            symbol: "AGCE_USDT",
            isAgce: true,
        },
        {
            name: "BTC",
            fullName: "Bitcoin",
            rate: "6.20%",
            icon: "/images/bitcoin_icon.png",
            symbol: "BTC_USDT",
        },
        {
            name: "ETH",
            fullName: "Ethereum",
            rate: "5.80%",
            icon: "/images/eth_currency_img.png",
            symbol: "ETH_USDT",
        },
        {
            name: "SOL",
            fullName: "Solana",
            rate: "7.15%",
            icon: "/images/coin_icon.svg",
            symbol: "SOL_USDT",
        },
        {
            name: "USDC",
            fullName: "USD Coin",
            rate: "9.30%",
            icon: "/images/tether_icon.png",
            symbol: "USDC_USDT",
        },
    ];

    const earningProducts = [
        {
            coin: "AGCX Flexible",
            icon: "/images/token-img/token_agce_icon.svg",
            apr: "10.50%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "AGCE_USDT",
            isAgce: true,
        },
        {
            coin: "Bitcoin Flexible",
            icon: "/images/bitcoin_icon.png",
            apr: "6.20%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "BTC_USDT",
        },
        {
            coin: "Ethereum Flexible",
            icon: "/images/eth_currency_img.png",
            apr: "5.80%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "ETH_USDT",
        },
        {
            coin: "Solana Flexible",
            icon: "/images/coin_icon.svg",
            apr: "7.15%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "SOL_USDT",
        },
        {
            coin: "AGCX Fixed 30D",
            icon: "/images/token-img/token_agce_icon.svg",
            apr: "4.50%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "AGCE_USDT",
            isAgce: true,
        },
        {
            coin: "BNB Flexible",
            icon: "/images/coin_icon2.svg",
            apr: "4.50%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "BNB_USDT",
        },
        {
            coin: "USDT Flexible",
            icon: "/images/tether_icon.png",
            apr: "3.20%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "USDT_USD",
        },
        {
            coin: "XRP Fixed 60D",
            icon: "/images/xrp_icon.png",
            apr: "8.90%",
            flexibility: "Flexible",
            status: "Active",
            symbol: "XRP_USDT",
        },
    ];

    const filteredProducts = earningProducts.filter((product) => {
        const matchesCoin =
            selectedCoinFilter === "All Coins" ||
            product.coin.toLowerCase().includes(selectedCoinFilter.toLowerCase());
        const matchesTerm =
            selectedTermFilter === "All Terms" ||
            (selectedTermFilter === "Flexible" && product.flexibility === "Flexible") ||
            (selectedTermFilter === "30 Days" && product.coin.includes("30D")) ||
            (selectedTermFilter === "60 Days" && product.coin.includes("60D"));
        return matchesCoin && matchesTerm;
    });

    const faqQuestionsLeft = [
        {
            id: "faq-1",
            question: "What is AGCX Earn?",
            answer: "AGCX Earn is a one-stop asset management platform providing flexible and fixed earning options, allowing users to earn attractive APR on their idle crypto assets with maximum security.",
        },
        {
            id: "faq-2",
            question: "What products are available on AGCX Earn?",
            answer: "AGCX Earn offers Flexible Terms (deposit and withdraw anytime), Fixed Lockup Terms (30D, 60D with higher yields), Smart Earn (automated portfolio optimization), and Auto Subscribe.",
        },
        {
            id: "faq-3",
            question: "How do I redeem my products on AGCX Earn?",
            answer: "For Flexible Terms, click 'Redeem' anytime and funds return instantly to your spot wallet. For Fixed Terms, funds automatically unlock and transfer to your spot account at maturity.",
        },
    ];

    const faqQuestionsRight = [
        {
            id: "faq-4",
            question: 'What does it mean to "pause and resume" products?',
            answer: "Pause and Resume lets users temporarily pause automated yield reinvestment or auto-subscription without liquidating or withdrawing their principal funds.",
        },
        {
            id: "faq-5",
            question: "When can I withdraw my funds?",
            answer: "Flexible term products allow 24/7 instant withdrawals. Fixed term products mature at the end of the lockup period, after which principal plus total yield is credited.",
        },
        {
            id: "faq-6",
            question: "Why do my returns fluctuate?",
            answer: "Yield rates are dynamically updated based on market demand, staking performance, and network rewards to provide sustainable and optimal returns.",
        },
    ];

    return (
        <>
            <Helmet>
                <title>AGCX Earn | Grow Your Crypto</title>
                <meta
                    name="description"
                    content="Grow your crypto with secure & flexible earning solutions. Earn up to 10.50% APR on AGCX, BTC, ETH, SOL and more."
                />
            </Helmet>

            <div className="earn-new-page">
                <div className="earn-container">

                    {/* ========================================== */}
                    {/* --- 1. HERO SECTION --- */}
                    {/* ========================================== */}
                    <div className="earn-hero">
                        <div className="earn-hero-left">
                            <h1 className="earn-hero-title">AGCX Earn</h1>
                            <p className="earn-hero-desc">
                                Grow your crypto with <span className="gold-text">secure & flexible</span> earning solutions.
                            </p>
                            <div className="earn-badges-row">
                                <div className="earn-badge-pill">
                                    <img src="/images/earn-img/earn_icon.svg" alt="Flexible Terms" />
                                    Flexible Terms
                                </div>
                                <div className="earn-badge-pill">
                                    <img src="/images/earn-img/earn_icon2.svg" alt="High Returns" />
                                    High Returns
                                </div>
                                <div className="earn-badge-pill">
                                    <img src="/images/earn-img/earn_icon3.svg" alt="Secure & Trusted" />
                                    Secure & Trusted
                                </div>
                            </div>
                            <Link to="/staking" className="btn-start-earning">
                                Start Earning Now →
                            </Link>
                        </div>

                        <div className="earn-hero-right">
                            <img
                                src="/images/earn-img/agce_earn_bnr.png"
                                alt="AGCX Earn 3D Illustration"
                            />
                        </div>
                    </div>

                    {/* ========================================== */}
                    {/* --- 2. TOP CRYPTO APY CARDS BAR --- */}
                    {/* ========================================== */}
                    <div className="earn-crypto-bar">
                        {topCryptoApyList.map((item, idx) => (
                            <div className="crypto-apy-card" key={idx}>
                                <div className="crypto-card-top">
                                    <img
                                        src={item.icon}
                                        alt={item.name}
                                        className={`crypto-coin-icon ${item.isAgce ? "agce-gold-bg" : ""}`}
                                    />
                                    <div className="crypto-info-name">
                                        <h4>{item.name}</h4>
                                        <span>{item.fullName}</span>
                                    </div>
                                </div>
                                <div className="crypto-card-bottom-row">
                                    <div>
                                        <div className="crypto-apy-rate">{item.rate}</div>
                                        <div className="crypto-apy-label">APR</div>
                                    </div>
                                    {/* <Link to={`/trade/${item.symbol}`} className="crypto-trade-link">
                                        Trade <i className="ri-arrow-right-line"></i>
                                    </Link> */}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ========================================== */}
                    {/* --- 3. FEATURE CARDS --- */}
                    {/* ========================================== */}
                    <div className="earn-features-grid">
                        <div className="earn-feature-card">
                            <div className="feature-card-icon">
                                <img src="/images/earn-img/smart_earn_icon.png" alt="Smart Earn" />
                            </div>
                            <div className="feature-card-content">
                                <h3>Smart Earn</h3>
                                <p>
                                    Let the system optimize allocations across products for maximizing earnings automatically.
                                </p>
                                <a href="#learn-more">Learn More</a>
                            </div>
                        </div>

                        <div className="earn-feature-card">
                            <div className="feature-card-icon">
                                <img src="/images/earn-img/subscribe_icon.png" alt="Auto Subscribe" />
                            </div>
                            <div className="feature-card-content">
                                <h3>Auto Subscribe</h3>
                                <p>
                                    Automatically subscribe the amount with one click on AGCX Earn at maturity.
                                </p>
                                <a href="#learn-more">Learn More</a>
                            </div>
                        </div>
                    </div>

                    {/* ========================================== */}
                    {/* --- 4. ALL EARNING PRODUCTS TABLE & CUSTOM DROPDOWNS --- */}
                    {/* ========================================== */}
                    <div className="all-products-section">
                        <div className="products-header-row">
                            <h2>All Earning Products</h2>
                            <div className="products-filters-right">
                                {/* Custom Coin Filter Dropdown */}
                                <div className="custom-dropdown-wrap" ref={coinDropdownRef}>
                                    <button
                                        type="button"
                                        className={`custom-dropdown-btn ${showCoinDropdown ? "open" : ""}`}
                                        onClick={() => {
                                            setShowCoinDropdown(!showCoinDropdown);
                                            setShowTermDropdown(false);
                                        }}
                                    >
                                        <span>{selectedCoinFilter}</span>
                                        <i className="ri-arrow-down-s-line"></i>
                                    </button>
                                    {showCoinDropdown && (
                                        <div className="custom-dropdown-menu">
                                            {coinOptions.map((opt) => (
                                                <div
                                                    key={opt}
                                                    className={`custom-dropdown-item ${selectedCoinFilter === opt ? "active" : ""}`}
                                                    onClick={() => {
                                                        setSelectedCoinFilter(opt);
                                                        setShowCoinDropdown(false);
                                                    }}
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Custom Term Filter Dropdown */}
                                <div className="custom-dropdown-wrap" ref={termDropdownRef}>
                                    <button
                                        type="button"
                                        className={`custom-dropdown-btn ${showTermDropdown ? "open" : ""}`}
                                        onClick={() => {
                                            setShowTermDropdown(!showTermDropdown);
                                            setShowCoinDropdown(false);
                                        }}
                                    >
                                        <span>{selectedTermFilter}</span>
                                        <i className="ri-arrow-down-s-line"></i>
                                    </button>
                                    {showTermDropdown && (
                                        <div className="custom-dropdown-menu">
                                            {termOptions.map((opt) => (
                                                <div
                                                    key={opt}
                                                    className={`custom-dropdown-item ${selectedTermFilter === opt ? "active" : ""}`}
                                                    onClick={() => {
                                                        setSelectedTermFilter(opt);
                                                        setShowTermDropdown(false);
                                                    }}
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button className="filter-search-btn">
                                    <i className="ri-search-line"></i>
                                </button>
                            </div>
                        </div>

                        <div className="products-table-card">
                            <table className="products-table">
                                <thead>
                                    <tr>
                                        <th>Coin</th>
                                        <th>APR</th>
                                        <th>Flexibility</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.length > 0 ? (
                                        filteredProducts.map((row, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <div className="coin-cell-info">
                                                        <img
                                                            src={row.icon}
                                                            alt={row.coin}
                                                            className={`coin-cell-icon ${row.isAgce ? "agce-gold-bg" : ""}`}
                                                        />
                                                        <span>{row.coin}</span>
                                                    </div>
                                                </td>
                                                <td className="apr-highlight">{row.apr}</td>
                                                <td>{row.flexibility}</td>
                                                <td>
                                                    <span className="status-badge-active">
                                                        <i className="ri-checkbox-circle-fill"></i> {row.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: "center", padding: "40px", color: "#9CA3AF" }}>
                                                No earning products found for selected filters.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="table-footer-btn-wrap">
                            <button className="btn-view-more-products">
                                View More Products
                            </button>
                        </div>
                    </div>

                    {/* ========================================== */}
                    {/* --- 5. FREQUENTLY ASKED QUESTIONS --- */}
                    {/* ========================================== */}
                    <div className="earn-faq-section">
                        <h2 className="faq-title">Frequently Asked Questions</h2>
                        <div className="faq-grid-2col">
                            <div className="faq-col">
                                {faqQuestionsLeft.map((faq) => (
                                    <div
                                        className={`faq-card-item ${openFaqId === faq.id ? "active" : ""}`}
                                        key={faq.id}
                                        onClick={() => toggleFaq(faq.id)}
                                    >
                                        <div className="faq-card-header">
                                            <p className="faq-card-question">{faq.question}</p>
                                            <span className="faq-plus-icon">+</span>
                                        </div>
                                        {openFaqId === faq.id && (
                                            <div className="faq-card-answer">
                                                <p>{faq.answer}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="faq-col">
                                {faqQuestionsRight.map((faq) => (
                                    <div
                                        className={`faq-card-item ${openFaqId === faq.id ? "active" : ""}`}
                                        key={faq.id}
                                        onClick={() => toggleFaq(faq.id)}
                                    >
                                        <div className="faq-card-header">
                                            <p className="faq-card-question">{faq.question}</p>
                                            <span className="faq-plus-icon">+</span>
                                        </div>
                                        {openFaqId === faq.id && (
                                            <div className="faq-card-answer">
                                                <p>{faq.answer}</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <Footer />
        </>
    );
};

export default EarnNewPage;
